import * as React from 'react';

import { useQuery } from '@apollo/client';

import { useParams } from 'js/lib/useRouter';

import { thirdPartyOrganizationsQuery } from 'bundles/enterprise-legacy-learner-home/queries/ThirdPartyOrganizationsQuery';
import type {
  ThirdPartyOrganizationsQuery,
  ThirdPartyOrganizationsQueryVariables,
} from 'bundles/enterprise-legacy-learner-home/queries/__generated__/ThirdPartyOrganizationsQuery';
import { OrgLandingProvider } from 'bundles/org-landing/components/OrgLandingContext';
import { useProductFeatures } from 'bundles/org-landing/productFeatureFlags';
import NotFound from 'bundles/phoenix/components/NotFound';
import { IdTypes } from 'bundles/product-features';

// Explicitly define the `children` props to make sure it is only ONE child
// `null` or `Array<React.ReactElement>` is not supported
type Props = {
  children: React.ReactElement;
};

function OrgLandingApp({ children }: Props) {
  const { orgSlug } = useParams();
  const { data, loading } = useQuery<ThirdPartyOrganizationsQuery, ThirdPartyOrganizationsQueryVariables>(
    thirdPartyOrganizationsQuery,
    {
      // @ts-expect-error TS2769 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      variables: { orgSlug },
    }
  );

  const thirdPartyOrganization = data?.ThirdPartyOrganizationsV1Resource.bySlug?.elements?.[0];

  const { features, loading: isFeaturesLoading } = useProductFeatures({
    idType: IdTypes.Organization,
    id: thirdPartyOrganization?.id || '',
    skip: !thirdPartyOrganization?.id,
    ssr: true,
  });

  // since we need to decide if this page 404 or not, don't show loading here
  if (loading || isFeaturesLoading) {
    return null;
  }

  if (!thirdPartyOrganization) {
    return <NotFound />;
  }

  if (!features.get('enterprise', 'enableOrgHomepage')) {
    return <NotFound />;
  }

  return (
    <OrgLandingProvider
      thirdPartyOrganization={thirdPartyOrganization}
      enableOrgHomepageAutoJoinProgram={features.get('enterprise', 'enableOrgHomepageAutoJoinProgram')}
    >
      {children}
    </OrgLandingProvider>
  );
}

export default OrgLandingApp;
